import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  Image as ImageIcon,
  Trash2,
  Loader2,
  Check,
  KeyRound,
  LogOut,
  Film,
} from "lucide-react";
import VideoStudio from "@/components/admin/VideoStudio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mp_admin_token";

export default function Admin() {
  const [token, setToken] = useState(
    typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) || "" : ""
  );
  const [tokenInput, setTokenInput] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", category: "" });
  const fileRef = useRef(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery`);
      setItems(res.data?.items || []);
    } catch {
      toast.error("Galerie konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const login = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    localStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
    setTokenInput("");
    toast.success("Eingeloggt");
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    toast.message("Ausgeloggt");
  };

  const onPickFile = () => fileRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!token) {
      toast.error("Bitte zuerst einloggen");
      return;
    }
    setUploading(true);
    let ok = 0;
    let fail = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title || file.name.replace(/\.[^.]+$/, ""));
        fd.append("category", form.category || "Werkstatt-Projekt");
        fd.append("admin_token", token);
        await axios.post(`${API}/gallery/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (err) {
        fail += 1;
        if (err.response?.status === 401) {
          toast.error("Token ungültig — bitte erneut einloggen");
          logout();
          break;
        }
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (ok) toast.success(`${ok} Bild${ok === 1 ? "" : "er"} hochgeladen`);
    if (fail) toast.error(`${fail} Bild${fail === 1 ? "" : "er"} fehlgeschlagen`);
    loadItems();
  };

  const deleteItem = async (id) => {
    if (!confirm("Bild wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { params: { admin_token: token } });
      toast.success("Gelöscht");
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#0e0e0e] border border-white/10 rounded-2xl p-8"
        >
          <Link
            to="/"
            data-testid="admin-back-link"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-[0.2em] font-mono mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Seite
          </Link>
          <div className="w-12 h-12 rounded-md bg-[#e52620] flex items-center justify-center mb-6">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Admin Login</h1>
          <p className="mt-2 text-zinc-500 text-sm">
            Geben Sie Ihr Admin-Token ein, um Galerie-Bilder hochzuladen.
          </p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin Token"
            className="mt-6 w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-4 py-3 text-white placeholder-zinc-600 transition-colors"
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-4 w-full bg-[#e52620] hover:bg-[#c10015] text-white font-bold uppercase tracking-[0.12em] text-sm px-5 py-3 rounded-md transition-colors active:scale-95"
          >
            Einloggen
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#0a0a0a] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="admin-back-link"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-[0.2em] font-mono"
            >
              <ArrowLeft className="w-4 h-4" />
              Zur Seite
            </Link>
            <span className="text-zinc-700">·</span>
            <h1 className="text-lg font-bold tracking-tight">
              Admin · Galerie verwalten
            </h1>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 rounded-md"
          >
            <LogOut className="w-3.5 h-3.5" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10 space-y-10">
        {/* Video Studio */}
        <VideoStudio token={token} />

        {/* Upload form */}
        <section className="bg-[#0e0e0e] border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-md bg-[#e52620] flex items-center justify-center">
              <Upload className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">Bilder hochladen</h2>
              <p className="text-zinc-500 text-sm">
                Mehrere Dateien gleichzeitig möglich · max. 12 MB pro Bild · jpg/png/webp/gif
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
                Titel (optional)
              </label>
              <input
                data-testid="admin-title-input"
                value={form.title}
                onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                placeholder="z. B. Ducati Custom-Lack"
                className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-4 py-2.5 text-white placeholder-zinc-600 transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 font-mono">
                Kategorie (optional)
              </label>
              <input
                data-testid="admin-category-input"
                value={form.category}
                onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
                placeholder="z. B. Lackierung / Carrosserie / Unfall"
                className="w-full bg-black/40 border border-white/10 focus:border-[#e52620] outline-none rounded-md px-4 py-2.5 text-white placeholder-zinc-600 transition-colors"
              />
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onFiles}
            className="hidden"
            data-testid="admin-file-input"
          />
          <button
            type="button"
            onClick={onPickFile}
            disabled={uploading}
            data-testid="admin-upload-button"
            className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#e52620] hover:bg-[#c10015] disabled:opacity-60 text-white font-bold uppercase tracking-[0.12em] text-sm px-7 py-4 rounded-md transition-all active:scale-95"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Lädt hoch…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Bilder auswählen
              </>
            )}
          </button>
        </section>

        {/* Existing items */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#e52620]" />
              Hochgeladene Bilder
              <span className="text-zinc-500 font-mono text-sm font-normal">
                ({items.length})
              </span>
            </h2>
            <button
              onClick={loadItems}
              data-testid="admin-refresh-button"
              className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-400 hover:text-white"
            >
              Aktualisieren
            </button>
          </div>

          {loading ? (
            <div className="text-zinc-500 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Lädt…
            </div>
          ) : items.length === 0 ? (
            <div className="text-zinc-500 text-sm border border-dashed border-white/10 rounded-xl p-12 text-center">
              Noch keine Bilder hochgeladen. Laden Sie oben das erste hoch.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((it) => (
                <div
                  key={it.id}
                  data-testid={`admin-item-${it.id}`}
                  className="group relative bg-[#0e0e0e] border border-white/10 rounded-xl overflow-hidden"
                >
                  <div className="relative aspect-[4/3] bg-black/40">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api${it.url}`}
                      alt={it.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#e52620] font-mono font-bold truncate">
                      {it.category}
                    </div>
                    <div className="mt-1 text-white font-bold text-sm tracking-tight truncate">
                      {it.title}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-600 font-mono">
                      <span>{Math.round((it.size || 0) / 1024)} KB</span>
                      <button
                        onClick={() => deleteItem(it.id)}
                        data-testid={`admin-delete-${it.id}`}
                        className="inline-flex items-center gap-1 text-zinc-500 hover:text-[#e52620] uppercase tracking-[0.15em] font-bold"
                      >
                        <Trash2 className="w-3 h-3" />
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <p className="mt-10 text-zinc-600 text-xs font-mono">
          <Check className="w-3 h-3 inline mr-1 text-[#e52620]" />
          Hochgeladene Bilder erscheinen automatisch in der öffentlichen Galerie auf der Startseite.
        </p>
      </main>
    </div>
  );
}
